import React from 'react';
import Modal from '../common/Modal';
import { 
  PlayIcon, 
  CogIcon, 
  ClockIcon, 
  BoltIcon,
  DocumentTextIcon,
  KeyIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

interface FlowBuilderDocumentationProps {
  isOpen: boolean;
  onClose: () => void;
}

const FlowBuilderDocumentation: React.FC<FlowBuilderDocumentationProps> = ({ isOpen, onClose }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="📚 Flow Builder Dokumentáció"
      size="5xl"
    >
      <div className="p-6 max-h-[70vh] overflow-y-auto">
        {/* Bevezetés */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🚀 Mi az a Flow Builder?
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            A <strong>Flow Builder</strong> egy vizuális automatizálási eszköz, amellyel egyszerűen hozhatod létre 
            üzleti folyamataidat kódolás nélkül. Húzd-és-ejtsd módszerrel építhetsz komplex munkafolyamatokat, 
            amelyek automatikusan futnak le megadott feltételek alapján.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              💡 <strong>Tipp:</strong> Kezdd egy egyszerű flow-val (pl. email küldés), majd fokozatosan bővítsd 
              bonyolultabb logikával!
            </p>
          </div>
        </section>

        {/* Főbb funkciók */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            ✨ Főbb Funkciók
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Trigger-ek */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-3 mb-2">
                <BoltIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Trigger-ek</h3>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Indítsd el a flow-kat manuálisan, ütemezetten (cron), vagy webhook-kal. 
                Automatizáld az ismétlődő feladatokat!
              </p>
            </div>

            {/* Akciók */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-3 mb-2">
                <PlayIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Akciók</h3>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Email küldés, HTTP kérések, adatbázis műveletek, PDF generálás és még sok más!
              </p>
            </div>

            {/* Feltételek */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-3 mb-2">
                <CogIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Feltételek</h3>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                If/Else logika, switch-case, ciklusok - építs komplex döntési fákat!
              </p>
            </div>

            {/* Ütemezés */}
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-3 mb-2">
                <ClockIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Ütemezés</h3>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Cron kifejezésekkel állíts be ismétlődő futtatásokat (pl. minden nap 9:00-kor).
              </p>
            </div>
          </div>
        </section>

        {/* Gyors útmutató */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🎯 Gyors Útmutató
          </h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Hozz létre egy új Flow-t
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Kattints a <strong>"+ Új Flow"</strong> gombra vagy válassz egy sablont a gyorsabb kezdéshez.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Adj hozzá egy Trigger-t
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Húzd be a bal oldali palettáról a <strong>Schedule Trigger</strong> vagy <strong>Webhook Trigger</strong> node-ot. 
                  Ez határozza meg, mikor induljon el a flow.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Építsd fel a logikát
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Húzz be további node-okat (Email, HTTP Request, stb.) és kösd össze őket. 
                  Kattints egy node-ra a konfigurálásához.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                4
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                  Mentsd és futtasd
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Kattints a <strong>"💾 Mentés"</strong> gombra, majd a <strong>"▶️ Futtatás"</strong> gombra a teszteléshez.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Node típusok */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🧩 Elérhető Node Típusok
          </h2>
          
          {/* Triggers */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
              <BoltIcon className="h-5 w-5 text-green-500" />
              Trigger-ek (Indítók)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">⏰ Schedule Trigger</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Cron alapú ütemezett futtatás</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">🔗 Webhook Trigger</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">HTTP kérésre induló flow</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">👆 Manual Trigger</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Kézi indítás gombbal</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
              <PlayIcon className="h-5 w-5 text-blue-500" />
              Akciók
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">📧 Send Email</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Email küldés SMTP-n keresztül</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">🌐 HTTP Request</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">API hívások (GET, POST, stb.)</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">💾 Database Query</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Firestore lekérdezések</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">📄 Generate PDF</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">PDF dokumentum generálás</p>
              </div>
            </div>
          </div>

          {/* Logic */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
              <CogIcon className="h-5 w-5 text-purple-500" />
              Logika
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">🔀 If/Else</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Feltételes elágazás</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">🔄 Loop</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Ismétlődő műveletek</p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700">
                <p className="font-medium text-gray-900 dark:text-gray-100">⏸️ Delay</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Várakozás (sleep)</p>
              </div>
            </div>
          </div>
        </section>

        {/* Hasznos tippek */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            💡 Hasznos Tippek
          </h2>
          <div className="space-y-3">
            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded">
              <p className="text-sm text-green-800 dark:text-green-200">
                <strong>🔑 API Kulcsok:</strong> Használd a <strong>"🔧 Eszközök → API Kulcsok"</strong> menüt 
                az érzékeny adatok biztonságos tárolására. Ezek titkosítva kerülnek mentésre!
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>👁️ Monitor:</strong> A <strong>"Monitor"</strong> gombbal real-time követheted a flow végrehajtását. 
                Ideális debuggoláshoz!
              </p>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4 rounded">
              <p className="text-sm text-purple-800 dark:text-purple-200">
                <strong>📊 Logok:</strong> Az <strong>"Execution Logok"</strong> menüben visszanézheted az összes 
                futtatás eredményét és hibáit.
              </p>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>⏰ Ütemezés:</strong> A Schedule Trigger cron formátumot használ. 
                Példa: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">0 9 * * *</code> = minden nap 9:00-kor
              </p>
            </div>
          </div>
        </section>

        {/* Példa flow-k */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            📝 Példa Flow-k
          </h2>
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                📧 Napi Email Riport
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Schedule Trigger (minden nap 9:00) → Database Query (adatok lekérése) → Generate PDF → Send Email
              </p>
              <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                Kezdő szint
              </span>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                🔗 Webhook Integráció
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Webhook Trigger → If/Else (adatok validálása) → HTTP Request (külső API) → Database Update
              </p>
              <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                Haladó szint
              </span>
            </div>
          </div>
        </section>

        {/* Támogatás */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            🆘 Támogatás
          </h2>
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="text-gray-700 dark:text-gray-300 mb-3">
              Ha elakadtál vagy kérdésed van:
            </p>
            <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span>📖</span>
                <span>Nézd meg a <strong>Flow Sablonokat</strong> inspirációért</span>
              </li>
              <li className="flex items-start gap-2">
                <span>🔍</span>
                <span>Használd a <strong>Monitor</strong> funkciót a hibakereséshez</span>
              </li>
              <li className="flex items-start gap-2">
                <span>📊</span>
                <span>Ellenőrizd az <strong>Execution Logokat</strong> a részletes hibaüzenetekért</span>
              </li>
            </ul>
          </div>
        </section>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800 rounded-b-lg">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            💡 Tipp: Nyomd meg az <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">F1</kbd> billentyűt bármikor a dokumentáció megnyitásához
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
          >
            Bezárás
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FlowBuilderDocumentation;
